import {
	Box,
	Chip,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	Theme,
} from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { useTranslation } from "react-i18next"

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8

const MenuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 250,
		},
	},
}

interface IstatusType {
	name: string
	bgColor: string
	color: string
}

const statusType: IstatusType[] = [
	{
		name: "ACCEPTED",
		bgColor: "#98bd0a",
		color: "#333",
	},
	{
		name: "AUTHORIZED",
		bgColor: "#007a4d",
		color: "#FFF",
	},
	{
		name: "CANCELED",
		bgColor: "#464646",
		color: "#FFF",
	},
	{
		name: "DRAFT",
		bgColor: "#e8c600",
		color: "#222",
	},
	{
		name: "IN_ERROR",
		bgColor: "#D32F2F",
		color: "#FFF",
	},
	{
		name: "NOT_EXECUTED",
		bgColor: "#A33F3F",
		color: "#FFF",
	},
	{
		name: "EXPIRED",
		bgColor: "#931F1F",
		color: "#FFF",
	},
	{
		name: "REJECTED",
		bgColor: "#c82269",
		color: "#FFF",
	},
	{
		name: "SUSPENDED",
		bgColor: "#c82269",
		color: "#FFF",
	},
	{
		name: "SUSPENDED_TEMP",
		bgColor: "#c82269",
		color: "#FFF",
	},
	{
		name: "NOT_TAKEN_OVER_BANK",
		bgColor: "#d73279",
		color: "#FFF",
	},
	{
		name: "NOT_TAKEN_OVER_BANK_GC",
		bgColor: "#c82269",
		color: "#FFF",
	},
	{
		name: "TO_AUTHORIZE",
		bgColor: "#893de7",
		color: "#FFF",
	},
	{
		name: "SUBMITTED",
		bgColor: "#5258e4",
		color: "#FFF",
	},
	{
		name: "EXECUTED",
		bgColor: "#5258e4",
		color: "#FFF",
	},
	{
		name: "SCT_WITHDRAWAL_REQUEST",
		bgColor: "#6b48e5",
		color: "#FFF",
	},
	{
		name: "SCT_REVERSAL_REQUEST",
		bgColor: "#0265dc",
		color: "#FFF",
	},
	{
		name: "SCT_WITHDRAWAL_SUCCESS",
		bgColor: "#00719f",
		color: "#FFF",
	},
	{
		name: "SCT_REVERSAL_SUCCESS",
		bgColor: "#0168be",
		color: "#FFF",
	},
	{
		name: "TO_ISSUE",
		bgColor: "#27bb36",
		color: "#FFF",
	},
	{
		name: "TAKEN_OVER_BANK",
		bgColor: "#007772",
		color: "#FFF",
	},
	{
		name: "TAKEN_OVER_BANK_GC",
		bgColor: "#109792",
		color: "#FFF",
	},
	{
		name: "TAKEN_OVER_PAYDO",
		bgColor: "#50D7D2",
		color: "#FFF",
	},
	{
		name: "TAKEN_OVER_PAYDO_GC",
		bgColor: "#40A7A2",
		color: "#FFF",
	},
	,
	{
		name: "NOT_SUBMITTED",
		bgColor: "#f68511",
		color: "#333",
	},
	{
		name: "NOT_AUTHORIZED",
		bgColor: "#d87521",
		color: "#333",
	},
]

function getStyles(
	name: string,
	statusSelected: readonly [IstatusType],
	theme: Theme
) {
	return {
		fontWeight: statusSelected.includes(name)
			? theme.typography.fontWeightRegular
			: theme.typography.fontWeightMedium,
	}
}

interface IStatusMultiSelectProps {
	statusSelected: string[]
	setStatusSelected: (statusList: string[]) => void
}

const StatusPlickMultiSelect = ({
	statusSelected,
	setStatusSelected,
}: IStatusMultiSelectProps) => {
	const theme = useTheme()
	const { t } = useTranslation()

	const handleChange = (event: SelectChangeEvent<typeof statusSelected>) => {
		const {
			target: { value },
		} = event

		setStatusSelected(
			// On autofill we get a stringified value.
			typeof value === "string" ? value.split(",") : value
		)
	}

	return (
		<FormControl sx={{ margin: 0, width: "100%" }} variant="standard">
			<InputLabel shrink={true} id="status-plick-multiple-chip-label">
				{t("status", { ns: "plick" })}
			</InputLabel>
			<Select
				labelId="status-plick-multiple-chip-label"
				id="status-plick-multiple-chip"
				variant="standard"
				multiple
				value={statusSelected}
				onChange={handleChange}
				// input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
				renderValue={(selectedArray: string[]) => {
					const displayArray = selectedArray.slice(0, 2)
					const remainingCount = selectedArray.length - displayArray.length
					return (
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
							{displayArray.map((value) => (
								<Chip
									key={value}
									label={value}
									sx={{
										color: statusType.find((status) => status.name === value)
											?.color,
										backgroundColor: statusType.find(
											(status) => status.name === value
										)?.bgColor,
									}}
								/>
							))}
							{remainingCount > 0 && <Chip label={`+${remainingCount}`} />}
						</Box>
					)
				}}
				MenuProps={MenuProps}
			>
				{statusType.map((currentStatus: IstatusType) => (
					<MenuItem
						key={currentStatus.name}
						value={currentStatus.name}
						style={getStyles(currentStatus.name, statusSelected, theme)}
					>
						{currentStatus.name}
					</MenuItem>
				))}
			</Select>
		</FormControl>
	)
}

export default StatusPlickMultiSelect
